import React, { useState, useRef, useEffect, useCallback } from "react";

const CoverSettingsTab = ({
  isDragging,
  setIsDragging,
  handleDrop,
  handleFileChange,
  previewUrl,
  posX,
  setPosX,
  posY,
  setPosY,
  scale,
  setScale,
  handleSave,
  isSaving,
  groupName,
}) => {
  const [isPinDragging, setIsPinDragging] = useState(false);
  const imgContainerRef = useRef(null);
  const imgElementRef = useRef(null);
  const fileInputRef = useRef(null);

  const albumTitle = groupName || "Album Title";

  // Calculate focal point from mouse/touch event
  const updateFocalPoint = useCallback(
    (clientX, clientY) => {
      const targetElement = imgElementRef.current || imgContainerRef.current;
      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      let xPercent = ((clientX - rect.left) / rect.width) * 100;
      let yPercent = ((clientY - rect.top) / rect.height) * 100;

      xPercent = Math.max(0, Math.min(100, Math.round(xPercent)));
      yPercent = Math.max(0, Math.min(100, Math.round(yPercent)));

      setPosX(xPercent);
      setPosY(yPercent);
    },
    [setPosX, setPosY]
  );

  const handlePointerDown = (e) => {
    e.preventDefault();
    setIsPinDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    updateFocalPoint(clientX, clientY);
  };

  useEffect(() => {
    if (!isPinDragging) return;

    const handlePointerMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      updateFocalPoint(clientX, clientY);
    };

    const handlePointerUp = () => {
      setIsPinDragging(false);
    };

    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", handlePointerUp);

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
      window.removeEventListener("touchmove", handlePointerMove);
      window.removeEventListener("touchend", handlePointerUp);
    };
  }, [isPinDragging, updateFocalPoint]);

  const handleResetCenter = () => {
    setPosX(50);
    setPosY(50);
    setScale(1);
  };

  return (
    <div className="settings-card">
      {/* Header */}
      <div className="settings-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div className="settings-card-icon">🎯</div>
          <div>
            <h2 className="settings-card-title" style={{ margin: 0 }}>Set Focal Point & Cover</h2>
            <p style={{ margin: 0, fontSize: "13px", color: "#64748b" }}>
              Click or drag the focal ring to position the main subject across desktop and mobile cards
            </p>
          </div>
        </div>

        {previewUrl && (
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={handleResetCenter}
              className="focal-action-pill-btn"
              title="Reset focal point to 50% center"
            >
              🔄 Center Point
            </button>

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="focal-action-pill-btn primary"
            >
              📤 Change Photo
            </button>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => e.target.files[0] && handleFileChange(e.target.files[0])}
      />

      {/* If No Image is Selected Yet: Show Full Dropzone */}
      {!previewUrl ? (
        <div style={{ padding: "30px 10px" }}>
          <label
            className={`settings-dropzone ${isDragging ? "dragging" : ""}`}
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 24px",
              border: "2px dashed #cbd5e1",
              borderRadius: "18px",
              background: isDragging ? "#eef2ff" : "#f8fafc",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) =>
                e.target.files[0] && handleFileChange(e.target.files[0])
              }
            />
            <span style={{ fontSize: "38px", marginBottom: "10px" }}>🖼️</span>
            <strong style={{ color: "#0f172a", fontSize: "16px", marginBottom: "4px" }}>
              Click or Drag & Drop Group Cover Image
            </strong>
            <span style={{ fontSize: "13px", color: "#64748b" }}>
              Upload a high quality photo to serve as the album cover card
            </span>
          </label>
        </div>
      ) : (
        /* If Image is Active: Show 2-Column Focal Point Editor */
        <div className="focal-point-editor-grid">
          {/* Left Column: Interactive Image with Draggable Focal Ring */}
          <div className="focal-left-panel">
            <div className="focal-panel-header">
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
                Interactive Focal Point
              </span>
              <span className="focal-coordinate-badge">
                X: {Math.round(posX)}% | Y: {Math.round(posY)}%
              </span>
            </div>

            <div
              className="focal-canvas-wrapper"
              ref={imgContainerRef}
              onMouseDown={handlePointerDown}
              onTouchStart={handlePointerDown}
            >
              <img
                ref={imgElementRef}
                src={previewUrl}
                alt="Original cover preview"
                className="focal-canvas-img"
                draggable={false}
              />

              {/* Draggable Focal Pin / Target Ring */}
              <div
                className={`focal-target-pin ${isPinDragging ? "dragging" : ""}`}
                style={{
                  left: `${posX}%`,
                  top: `${posY}%`,
                }}
              >
                <div className="focal-pin-ring">
                  <div className="focal-pin-center-dot" />
                </div>
                <div className="focal-pin-tooltip">Focal Point</div>
              </div>
            </div>

            <p className="focal-helper-text">
              👆 <strong>Click or drag</strong> the ring on the photo to set the main subject / face.
            </p>

            {/* Optional Zoom Fine Tuning */}
            <div className="focal-zoom-control">
              <div className="focal-zoom-header">
                <span>🔍 Zoom In</span>
                <span>{scale.toFixed(1)}x</span>
              </div>
              <input
                type="range"
                min="1"
                max="2.5"
                step="0.05"
                value={scale}
                onChange={(e) => setScale(parseFloat(e.target.value))}
                className="focal-zoom-slider"
              />
            </div>
          </div>

          {/* Right Column: Live Dual Previews (Desktop 16:9 and Mobile 4:5) */}
          <div className="focal-right-panel">
            <div className="focal-panel-header">
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e293b" }}>
                Live Previews
              </span>
              <span style={{ fontSize: "12px", color: "#10b981", fontWeight: 600 }}>
                ✨ Real-time Adapt
              </span>
            </div>

            <div className="focal-previews-container">
              {/* Desktop Card Preview (16:9 Landscape) */}
              <div className="focal-preview-card desktop">
                <div className="focal-device-label">Desktop View</div>
                <div className="focal-card-mockup">
                  <div className="focal-card-media-wrapper desktop-aspect">
                    <img
                      src={previewUrl}
                      alt="Desktop Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: `${posX}% ${posY}%`,
                        transform: `scale(${scale})`,
                        transformOrigin: `${posX}% ${posY}%`,
                        transition: isPinDragging ? "none" : "object-position 0.15s ease, transform 0.15s ease",
                      }}
                    />
                  </div>
                  <div className="focal-card-footer">
                    <span className="focal-card-title">{albumTitle}</span>
                    <div className="focal-card-actions">
                      <span title="Settings">⚙️</span>
                      <span title="Share">🔗</span>
                      <span title="Download">⬇️</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mobile Card Preview (4:5 Portrait) */}
              <div className="focal-preview-card mobile">
                <div className="focal-device-label">Mobile View</div>
                <div className="focal-card-mockup">
                  <div className="focal-card-media-wrapper mobile-aspect">
                    <img
                      src={previewUrl}
                      alt="Mobile Preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: `${posX}% ${posY}%`,
                        transform: `scale(${scale})`,
                        transformOrigin: `${posX}% ${posY}%`,
                        transition: isPinDragging ? "none" : "object-position 0.15s ease, transform 0.15s ease",
                      }}
                    />
                  </div>
                  <div className="focal-card-footer">
                    <span className="focal-card-title">{albumTitle}</span>
                    <div className="focal-card-actions">
                      <span title="Settings">⚙️</span>
                      <span title="Share">🔗</span>
                      <span title="Download">⬇️</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button Footer */}
      <div className="settings-actions-footer" style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          className="settings-save-btn"
          onClick={handleSave}
          disabled={isSaving}
          style={{
            background: "#4f46e5",
            color: "#ffffff",
            border: "none",
            borderRadius: "12px",
            padding: "12px 24px",
            fontWeight: 700,
            fontSize: "14px",
            cursor: isSaving ? "not-allowed" : "pointer",
            boxShadow: "0 4px 12px rgba(79, 70, 229, 0.25)",
            transition: "all 0.2s ease",
          }}
        >
          {isSaving ? "Saving..." : "Save Cover Settings"}
        </button>
      </div>
    </div>
  );
};

export default CoverSettingsTab;
